import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { teamAPI, teamPodcastScopeAPI } from "../../lib/api";
import type { TeamPodcastScope } from "../../lib/api";
import type { TeamResponse } from "../../types/api";
import { Button } from "../common";
import styles from "./TeamAccessSection.module.css";

const ALL_PERMISSIONS = [
  "manage_episodes",
  "manage_settings",
  "view_analytics",
  "manage_subscribers",
] as const;

const PERMISSION_LABELS: Record<string, string> = {
  manage_episodes: "Manage Episodes",
  manage_settings: "Manage Settings",
  view_analytics: "View Analytics",
  manage_subscribers: "Manage Subscribers",
};

interface TeamAccessSectionProps {
  organizationId: string;
  podcastId: string;
  userRole: string | null;
}

export function TeamAccessSection({ organizationId, podcastId, userRole }: TeamAccessSectionProps) {
  const [scopes, setScopes] = useState<TeamPodcastScope[]>([]);
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [localPermissions, setLocalPermissions] = useState<Record<string, string[]>>({});

  const canEdit = userRole === "owner" || userRole === "admin";

  const loadData = useCallback(async () => {
    try {
      const [scopesResult, teamsResult] = await Promise.all([
        teamPodcastScopeAPI.list(organizationId, podcastId),
        teamAPI.getAll(organizationId),
      ]);
      setScopes(scopesResult);
      setTeams(teamsResult);
      const perms: Record<string, string[]> = {};
      for (const scope of scopesResult) {
        perms[scope.team_id] = [...scope.permissions];
      }
      setLocalPermissions(perms);
    } catch {
      toast.error("Failed to load team access data");
    } finally {
      setLoading(false);
    }
  }, [organizationId, podcastId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const assignedTeamIds = new Set(scopes.map((s) => s.team_id));
  const availableTeams = teams.filter((t) => !assignedTeamIds.has(t.id));

  const handleAdd = async () => {
    if (!selectedTeamId) return;
    setAdding(true);
    try {
      const scope = await teamPodcastScopeAPI.assign(organizationId, podcastId, selectedTeamId, [
        "view_analytics",
      ]);
      setScopes((prev) => [...prev, scope]);
      setLocalPermissions((prev) => ({ ...prev, [scope.team_id]: [...scope.permissions] }));
      setSelectedTeamId("");
      setShowAddForm(false);
      toast.success("Team access added");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add team access");
    } finally {
      setAdding(false);
    }
  };

  const handleTogglePermission = (teamId: string, permission: string) => {
    setLocalPermissions((prev) => {
      const current = prev[teamId] || [];
      const next = current.includes(permission)
        ? current.filter((p) => p !== permission)
        : [...current, permission];
      return { ...prev, [teamId]: next };
    });
  };

  const handleSave = async (teamId: string) => {
    const permissions = localPermissions[teamId] || [];
    if (permissions.length === 0) {
      toast.error("Select at least one permission");
      return;
    }
    setSaving(teamId);
    try {
      await teamPodcastScopeAPI.update(organizationId, podcastId, teamId, permissions);
      setScopes((prev) => prev.map((s) => (s.team_id === teamId ? { ...s, permissions } : s)));
      toast.success("Permissions updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update permissions");
    } finally {
      setSaving(null);
    }
  };

  const handleRemove = async (teamId: string) => {
    setRemoving(teamId);
    try {
      await teamPodcastScopeAPI.remove(organizationId, podcastId, teamId);
      setScopes((prev) => prev.filter((s) => s.team_id !== teamId));
      setLocalPermissions((prev) => {
        const next = { ...prev };
        delete next[teamId];
        return next;
      });
      toast.success("Team access removed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove team access");
    } finally {
      setRemoving(null);
    }
  };

  const getTeamName = (teamId: string) => {
    const team = teams.find((t) => t.id === teamId);
    return team?.name || teamId;
  };

  const hasChanges = (teamId: string) => {
    const scope = scopes.find((s) => s.team_id === teamId);
    if (!scope) return false;
    const local = localPermissions[teamId] || [];
    if (local.length !== scope.permissions.length) return true;
    return !local.every((p) => scope.permissions.includes(p));
  };

  if (loading) {
    return (
      <div className={styles.section}>
        <h2>Team Access</h2>
        <p className={styles.loading}>Loading team access...</p>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div>
          <h2>Team Access</h2>
          <p className={styles.sectionDescription}>
            Control which teams can access this podcast and what they can do.
          </p>
        </div>
        {canEdit && !showAddForm && availableTeams.length > 0 && (
          <Button variant="secondary" onClick={() => setShowAddForm(true)}>
            Add Team
          </Button>
        )}
      </div>

      {showAddForm && (
        <div className={styles.addForm}>
          <div className={styles.addFormRow}>
            <select
              className={styles.teamSelect}
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              disabled={adding}
            >
              <option value="">Select a team...</option>
              {availableTeams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
            <Button
              variant="primary"
              onClick={handleAdd}
              loading={adding}
              loadingText="Adding..."
              disabled={!selectedTeamId}
            >
              Add
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddForm(false);
                setSelectedTeamId("");
              }}
              disabled={adding}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {scopes.length === 0 && !showAddForm ? (
        <div className={styles.emptyState}>
          <p>No teams have access to this podcast.</p>
          <p className={styles.sectionDescription}>
            Add a team to grant members access to this podcast.
          </p>
        </div>
      ) : (
        <div className={styles.teamList}>
          {scopes.map((scope) => (
            <div key={scope.id} className={styles.teamCard}>
              <div className={styles.teamHeader}>
                <span className={styles.teamName}>{getTeamName(scope.team_id)}</span>
                {canEdit && (
                  <div className={styles.teamActions}>
                    {hasChanges(scope.team_id) && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSave(scope.team_id)}
                        loading={saving === scope.team_id}
                        loadingText="Saving..."
                      >
                        Save
                      </Button>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemove(scope.team_id)}
                      loading={removing === scope.team_id}
                      loadingText="Removing..."
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>
              <div className={styles.permissionList}>
                {ALL_PERMISSIONS.map((perm) => (
                  <label key={perm} className={styles.permissionLabel}>
                    <input
                      type="checkbox"
                      checked={(localPermissions[scope.team_id] || []).includes(perm)}
                      onChange={() => handleTogglePermission(scope.team_id, perm)}
                      disabled={!canEdit}
                    />
                    <span>{PERMISSION_LABELS[perm]}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
